// Modal tạo/sửa bài đăng kênh cộng đồng: nội dung plain text + tối đa 6 ảnh
// + danh mục + ghim. Ảnh cũ (edit) hiển thị bằng url, ảnh mới bằng originFileObj
// (beforeUpload={()=>false} => antd không tự upload, ta build FormData thủ công).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Form, Input, Select, Switch, Upload, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import communityApi from "../../../api/communityApi";
import { AudienceFields, audiencePayload } from "../../../components/audience-fields";
import COMMUNITY_BG_PRESETS, { BG_MAX_CONTENT_LENGTH } from "./community-bg-presets";
import CommunityBgPresetPicker from "./community-bg-preset-picker";
import CommunityMarkdownToolbar from "./community-markdown-toolbar";
import CommunityContentPreview from "./community-content-preview";

const API_IMG_URL = process.env.REACT_APP_API_PUBLIC_URL || "";
const MAX_IMAGES = 6;
const BG_PRESET_KEYS = Object.keys(COMMUNITY_BG_PRESETS);

export default function CommunityPostForm({ open, onClose, onSuccess, editingPost, categories }) {
    const [form] = Form.useForm();
    const contentRef = useRef(null);
    const [fileList, setFileList] = useState([]);
    const [saving, setSaving] = useState(false);
    const [bgPreset, setBgPreset] = useState(null);
    const isEdit = Boolean(editingPost);

    // Nền chữ chỉ hợp lệ khi KHÔNG có ảnh + nội dung ngắn — khớp rule server
    // (resolveBgPreset trong communityAdminController.js).
    const contentValue = Form.useWatch("content", form) || "";
    const canUseBg = fileList.length === 0 && String(contentValue).trim().length <= BG_MAX_CONTENT_LENGTH;

    // URL xem trước theo ĐÚNG thứ tự ảnh của bài — khối `![N]` trong nội dung trỏ
    // tới ảnh thứ N. Ảnh mới chưa upload thì lấy blob tạm (antd đặt ở thumbUrl).
    const imageUrls = useMemo(
        () =>
            fileList.map(
                (file) =>
                    file.url ||
                    file.thumbUrl ||
                    (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "")
            ),
        [fileList]
    );

    // Thu hồi blob tạm khi danh sách ảnh đổi / đóng modal (tránh giữ bộ nhớ)
    useEffect(() => {
        return () => imageUrls.forEach((url) => url.startsWith("blob:") && URL.revokeObjectURL(url));
    }, [imageUrls]);

    useEffect(() => {
        if (!open) return;
        if (editingPost) {
            form.setFieldsValue({
                title: editingPost.title,
                content: editingPost.content,
                category: editingPost.category,
                pinned: editingPost.pinned === 1 || editingPost.pinned === true,
                discountCode: editingPost.discountCode || "",
                audience: editingPost.audience || "all",
                audienceDays: editingPost.audienceDays ?? null,
            });
            setFileList(
                (editingPost.images || []).map((filename) => ({
                    uid: filename,
                    name: filename,
                    status: "done",
                    url: `${API_IMG_URL}/img/post-thumbs/${filename}`,
                }))
            );
            setBgPreset(
                editingPost.bgPreset && BG_PRESET_KEYS.includes(editingPost.bgPreset) ? editingPost.bgPreset : null
            );
        } else {
            form.resetFields();
            form.setFieldsValue({ pinned: false, audience: "all" });
            setFileList([]);
            setBgPreset(null);
        }
    }, [open, editingPost]); // eslint-disable-line react-hooks/exhaustive-deps

    // Thêm ảnh hoặc nội dung vượt quá giới hạn -> tự bỏ chọn nền (khớp rule server)
    useEffect(() => {
        if (!canUseBg && bgPreset) setBgPreset(null);
    }, [canUseBg]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFileChange = ({ fileList: newList }) => {
        setFileList(newList.slice(0, MAX_IMAGES));
    };

    // Dán ảnh từ clipboard (Ctrl+V bất kỳ đâu trong modal) → vào thẳng danh sách
    // ảnh, khỏi phải lưu file rồi bấm Thêm ảnh (góp ý 29/07). antd tự sinh
    // thumbnail từ originFileObj cho listType picture-card.
    useEffect(() => {
        if (!open) return undefined;
        const handlePaste = (e) => {
            const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
            if (!files.length) return;
            e.preventDefault();
            setFileList((prev) => {
                const room = MAX_IMAGES - prev.length;
                if (room <= 0) {
                    message.warning(`Tối đa ${MAX_IMAGES} ảnh`);
                    return prev;
                }
                const stamp = Date.now();
                const items = files.slice(0, room).map((file, i) => ({
                    uid: `paste-${stamp}-${i}`,
                    name: file.name || `paste-${stamp}-${i}.png`,
                    status: "done",
                    originFileObj: file,
                }));
                return [...prev, ...items];
            });
        };
        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [open]);

    const handleSubmit = async (values) => {
        if (fileList.length === 0 && !bgPreset) {
            message.error("Vui lòng chọn ít nhất 1 ảnh hoặc chọn nền chữ");
            return;
        }
        try {
            setSaving(true);
            const formData = new FormData();
            if (values.title && values.title.trim()) formData.append("title", values.title.trim());
            formData.append("content", values.content.trim());
            formData.append("category", values.category);
            formData.append("pinned", values.pinned ? "true" : "false");
            formData.append("bgPreset", bgPreset || "");
            const audience = audiencePayload(values);
            formData.append("audience", audience.audience);
            formData.append("audienceDays", audience.audienceDays);
            // Luôn gửi field (kể cả rỗng) để backend biết "xoá mã" khi edit;
            // nếu chỉ gửi khi có giá trị thì không bao giờ clear được mã cũ.
            formData.append("discountCode", (values.discountCode || "").trim().toUpperCase());

            const keepImages = [];
            fileList.forEach((file) => {
                if (file.originFileObj) {
                    formData.append("images", file.originFileObj);
                } else {
                    keepImages.push(file.name);
                }
            });

            if (isEdit) {
                formData.append("postId", editingPost.postId);
                formData.append("keepImages", JSON.stringify(keepImages));
                await communityApi.updatePost(formData);
                message.success("Đã cập nhật bài đăng");
            } else {
                await communityApi.createPost(formData);
                message.success("Đã đăng bài mới");
            }
            onSuccess?.();
            onClose();
        } catch (error) {
            message.error(error.response?.data?.message || "Có lỗi xảy ra, thử lại sau");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={isEdit ? "Sửa bài đăng" : "Đăng deal mới"}
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText={isEdit ? "Cập nhật" : "Đăng bài"}
            cancelText="Hủy"
            confirmLoading={saving}
            width={640}
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                    label="Nội dung"
                    name="content"
                    className="!mb-2"
                    rules={[{ required: true, message: "Nhập nội dung bài đăng" }]}
                >
                    <Input.TextArea
                        ref={contentRef}
                        rows={6}
                        placeholder="Nội dung deal (dán link được; bôi đen rồi bấm nút trên thanh công cụ để in đậm/nghiêng, tạo tiêu đề, gạch đầu dòng, chèn ảnh của bài...)"
                    />
                </Form.Item>
                <CommunityMarkdownToolbar
                    textareaRef={contentRef}
                    value={contentValue}
                    onChange={(nextValue) => form.setFieldValue("content", nextValue)}
                    imageCount={fileList.length}
                />

                {/* Có nền chữ thì preview đã nằm trong picker — không hiện 2 khung trùng nhau */}
                {!bgPreset && <CommunityContentPreview text={contentValue} imageUrls={imageUrls} />}

                {canUseBg ? (
                    <CommunityBgPresetPicker value={bgPreset} onChange={setBgPreset} previewText={contentValue} />
                ) : (
                    <div className="mb-4 -mt-2 text-xs text-content-3">
                        Nền chỉ áp dụng cho bài ngắn (≤{BG_MAX_CONTENT_LENGTH} ký tự) không ảnh.
                    </div>
                )}

                <Form.Item label="Tiêu đề" name="title">
                    <Input placeholder="Tự lấy từ nội dung nếu bỏ trống" />
                </Form.Item>

                <div className="flex gap-4">
                    <Form.Item
                        label="Danh mục"
                        name="category"
                        className="flex-1"
                        rules={[{ required: true, message: "Chọn danh mục" }]}
                    >
                        <Select
                            placeholder="Chọn danh mục"
                            options={(categories || []).map((c) => {
                                // Tương thích 2 dạng: string | { name, icon }
                                const name = typeof c === "string" ? c : c.name;
                                return { label: `#${name}`, value: name };
                            })}
                        />
                    </Form.Item>
                    <Form.Item label="Ghim bài" name="pinned" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </div>

                <AudienceFields extra="Bài chỉ hiện trong feed của đúng đối tượng; thông báo cũng chỉ gửi cho họ." />

                <Form.Item
                    label="Mã giảm giá (tùy chọn)"
                    name="discountCode"
                    extra="Nhiều mã cách nhau dấu phẩy (tối đa 20 mã), mỗi mã hiện 1 chip copy riêng — feed hiện 4 chip đầu, còn lại thu vào nút '+N mã'."
                >
                    <Input
                        maxLength={1000}
                        placeholder="VD: SHOPEE2025, SALE50K"
                        onChange={(e) => form.setFieldValue("discountCode", e.target.value.toUpperCase())}
                    />
                </Form.Item>

                <Form.Item label={`Ảnh (tối đa ${MAX_IMAGES})`} required={!bgPreset}>
                    <Upload
                        listType="picture-card"
                        multiple
                        maxCount={MAX_IMAGES}
                        fileList={fileList}
                        beforeUpload={() => false}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png,image/webp"
                    >
                        {fileList.length >= MAX_IMAGES ? null : (
                            <div>
                                <FontAwesomeIcon icon={faPlus} />
                                <div className="mt-1">Thêm ảnh</div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>
            </Form>
        </Modal>
    );
}
